import * as React from 'react';
import { RaRecord, useNotify, useRedirect } from 'react-admin';
import {
    Box,
    Typography,
    Button,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    FormHelperText,
    useMediaQuery,
  MenuItem} from '@mui/material';
import { PageHeader } from '../../components/page-header/PageHeader';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { FormBackButton } from '../../components/back-button/FormBackButton';
import { ScrollToFirstError } from '../../components/form/ScrollToFirstError';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '../../components/icon-button/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { TypeOfCuisine } from '../../enums/TypeOfCuisine';
import { Language } from '../../enums/Language';
import { getBackendLanguage, getLanguageOptions } from '../../core/i18n';
import TranslateService from '../../services/translate/translateService';
import {
    TranslationsSection,
    TranslationsMap,
    TranslationSnapshot,
    AiTranslationResult,
    buildTranslationPayload,
} from '../../components/translations/TranslationsSection';
import { watchTranslations } from '../../core/translations/translationStatus';
import { WeekDay } from '../../enums/weekDay';
import { LocationType } from '../../enums/LocationType';
import { CurrencyCode } from '../../enums/CurrencyCode';
import { isSupportedCountry } from '../../enums/CountryCode';
import { MonthlyFeeDefaultService } from '../../services/billing/monthlyFeeDefaultService';
import { formatMoney } from '../coupons/couponTypes';
import { RestaurantService } from '../../services/restaurant/restaurantService';
import { useParams } from 'react-router-dom';
import { useJsApiLoader, StandaloneSearchBox } from '@react-google-maps/api';
import { ImageCropDialog } from '../../components/dialogs/ImageCropDialog';
import { labelAboveSx } from '../../core/theme/formStyles';
import * as Sentry from '@sentry/react';
import { isAdminRole } from '../../utils/roles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { DotykackaSection } from '../../components/dotykacka/DotykackaSection';

// Matches the backend @Size(max=500) on CreateRestaurantRequest.description.
/**
 * Skleja czesci adresu, pomijajac te, ktorych Google Places nie zwrocilo.
 *
 * Wczesniej bylo tu `${route} ${streetNumber}` — gdy Places nie oddalo
 * `street_number` (typowe dla wyniku na poziomie ulicy), do stringa wchodzil
 * literalny "undefined" i w tej postaci trafial do bazy. `.trim()` tego nie
 * lapal, bo undefined siedzialo w SRODKU. W bazie zostaly po tym rekordy
 * w rodzaju "aleja Grunwaldzka undefined".
 */
const joinAddressParts = (separator: string, ...parts: (string | undefined | null)[]): string =>
    parts
        .map((part) => (part == null ? '' : String(part).trim()))
        .filter((part) => part !== '')
        .join(separator);

const DESCRIPTION_MAX_LENGTH = 500;

const CURRENCY_CODE = Object.entries(CurrencyCode).map(([id, value]) => ({ id, value }));

const monthlyFeeDefaultService = new MonthlyFeeDefaultService();

const GOOGLE_MAPS_LIBRARIES: 'places'[] = ['places'];

export default function LocationsCreate(): JSX.Element {
    const { t } = useTranslation();

    const cuisineLabels: Record<string, string> = {
        POLISH: t('enums.typeOfCuisine.POLISH'),
        CHINESE: t('enums.typeOfCuisine.CHINESE'),
        AMERICAN: t('enums.typeOfCuisine.AMERICAN'),
        VIETNAMESE: t('enums.typeOfCuisine.VIETNAMESE'),
        ITALIAN: t('enums.typeOfCuisine.ITALIAN'),
        MEXICAN: t('enums.typeOfCuisine.MEXICAN'),
        INDIAN: t('enums.typeOfCuisine.INDIAN'),
        JAPANESE: t('enums.typeOfCuisine.JAPANESE'),
        THAI: t('enums.typeOfCuisine.THAI'),
        FRENCH: t('enums.typeOfCuisine.FRENCH'),
        GREEK: t('enums.typeOfCuisine.GREEK'),
        KOREAN: t('enums.typeOfCuisine.KOREAN'),
        TURKISH: t('enums.typeOfCuisine.TURKISH'),
        MIXED: t('enums.typeOfCuisine.MIXED'),
    };

    const TYPE_OF_CUISINE = Object.keys(TypeOfCuisine).map((id) => ({
        id,
        name: cuisineLabels[id] ?? TypeOfCuisine[id as keyof typeof TypeOfCuisine],
    }));

    const LANGUAGE = getLanguageOptions(t);

    const LOCATION_TYPE = Object.keys(LocationType).map((id) => ({
        id,
        name: t('enums.locationType.RESTAURANT'),
    }));
    const translateService = new TranslateService();
    const [autoTranslate, setAutoTranslate] = React.useState(true);
    const [translations, setTranslations] = React.useState<TranslationsMap>({});
    const [lastSnapshot, setLastSnapshot] = React.useState<TranslationSnapshot | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState<RaRecord | null>(null);
    const [address, setAddress] = React.useState<any>(null);
    const [isAddressComplete, setIsAddressComplete] = React.useState(false);

    const addressInputRef = React.useRef<any>(null);
    const lastSelectedAddressRef = React.useRef("");

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY ?? '',
        libraries: GOOGLE_MAPS_LIBRARIES,
    })

    console.log(isLoaded);

    const { id } = useParams();
    const isEditMode = Boolean(id);

    const restaurantService = new RestaurantService();

    // A stored translation may be keyed by language id ("ENG"), by the
    // localized label shown in the dropdown ("Angielski"), or by the enum
    // value the translate endpoint returns ("English"), so accept all three.
    const resolveLanguageId = (translationLanguage: string) =>
        LANGUAGE.find((l) =>
            translationLanguage === l.id ||
            translationLanguage === l.name ||
            translationLanguage === Language[l.id as keyof typeof Language]
        )?.id;

    const requestAiTranslation = async (sourceLanguage: string, description: string): Promise<AiTranslationResult> => {
        try {
            const response = await translateService.translateLocation({
                defaultLanguage: sourceLanguage,
                description,
            });
            const result: AiTranslationResult = {};
            (response as any[]).forEach((tr) => {
                const languageId = resolveLanguageId(tr.language);
                if (languageId && languageId !== sourceLanguage) {
                    result[languageId] = { description: tr.translatedDescription ?? tr.translation ?? '' };
                }
            });
            notify(t('notifications.translationCompleted'), { type: 'success' });
            return result;
        } catch (error: any) {
            notify(error.response?.data?.error, { type: 'error' });
            console.error(error.response?.data?.error);
            Sentry.captureException(error);
            throw error;
        }
    };

    React.useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            (async () => {
                try {
                    const data = await restaurantService.getRestaurants(id ? Number(id) : undefined);
                    console.log("data: " + JSON.stringify(data));
                    setData(data);
                    setPreviewUrl(data.imageUrl);
                    const loaded: TranslationsMap = {};
                    (data.descriptionTranslations || []).forEach((tr: any) => {
                        const languageId = resolveLanguageId(tr.language);
                        const text = tr.translatedDescription ?? tr.translation ?? '';
                        if (languageId && languageId !== data.language && text) {
                            /* `source` musi przetrwac przeladowanie strony — inaczej reczna
                               poprawka wrocilaby jako AI i pierwszy zapis oddalby ja
                               automatowi do nadpisania. `aiText` (punkt powrotu przycisku
                               "przywroc wersje AI") ma sens tylko dla tekstu od AI. */
                            loaded[languageId] = tr.source === 'MANUAL'
                                ? { description: { text, source: 'manual' } }
                                : { description: { text, source: 'ai', aiText: text } };
                        }
                    });
                    setTranslations(loaded);
                    setAutoTranslate(data.autoTranslate !== false);
                    if (Object.keys(loaded).length > 0) {
                        setLastSnapshot({ language: data.language, values: { description: data.description || '' } });
                    }


                    const addr = data.address;

                    const formatted = {
                        streetWithNo: addr.streetWithNo || "",
                        postalCode: addr.postalCode || "",
                        city: addr.city || "",
                        state: addr.state || "",
                        district: addr.district || "",
                        latitude: addr.latitude?.toString() || "",
                        longitude: addr.longitude?.toString() || "",
                    };

                    setAddress(formatted);
                    setIsAddressComplete(true);
                    lastSelectedAddressRef.current = joinAddressParts(' ', addr.streetWithNo, addr.city, addr.postalCode);



                    setIsLoading(false);
                } catch (error: any) {
                    notify(error.response.data.error, { type: 'error' });
                    console.error(error.response.data.error);
                    setIsLoading(false);
                    Sentry.captureException(error);
                }
            })();
        }
    }, [isEditMode, id]);

    //TODO: it will be needed later when location types endpoint will be ready
    // React.useEffect(() => {
    //     let isActive = true;
    //     (async () => {
    //         try {
    //             const res = await locationService.getLocationTypes();
    //             console.log(res);
    //             if (isActive) setLocationTypes(res);
    //         } catch (e) {
    //             console.error('Failed to load location types', e);
    //             Sentry.captureException(e);
    //         }
    //     })();
    //     return () => { isActive = false; };
    // }, []);

    const [fileBlob, setFileBlob] = React.useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [cropDialogOpen, setCropDialogOpen] = React.useState(false);
    const [rawImageSrc, setRawImageSrc] = React.useState<string | null>(null);
    const notify = useNotify();
    const redirect = useRedirect();

    const isAdmin = isAdminRole();

    // Domyslny abonament na kazda walute - podpowiedz pod polem "Oplata restauracji" (tylko admin).
    const [feeDefaults, setFeeDefaults] = React.useState<Record<string, number | null>>({});
    React.useEffect(() => {
        if (!isAdmin) return;
        monthlyFeeDefaultService.list()
            .then((defaults) => setFeeDefaults(
                Object.fromEntries(defaults.map((d) => [d.currency, d.monthlyFee]))
            ))
            .catch((error) => Sentry.captureException(error));
    }, [isAdmin]);

    const monthlyFeeHint = (currency: string): string => {
        if (!currency) return ' ';
        const amount = feeDefaults[currency];
        return amount == null
            ? t('locationsManagement.create.monthlyFeeNoDefault')
            : t('locationsManagement.create.monthlyFeeDefaultHint', { amount: formatMoney(amount, currency) });
    };

    React.useEffect(() => {
        if (!isEditMode && !isAdmin) {
            notify(t('notifications.notAuthorized'), { type: 'error' });
            redirect("/locationsManagement");
        }
    }, [isEditMode, isAdmin]);

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    };

    const handleFile = (file: File) => {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            notify(t('notifications.invalidImageFormat'), { type: 'error' });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result as string);
            setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = (croppedBlob: Blob) => {
        setFileBlob(croppedBlob);
        setPreviewUrl(URL.createObjectURL(croppedBlob));
        setCropDialogOpen(false);
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        if (!fileBlob) {
            setRawImageSrc(null);
        }
    };

    const handleRemove = () => {
        setFileBlob(null);
        setPreviewUrl(null);
        setRawImageSrc(null);
    };

    const weekDayLabels: Record<string, string> = {
        SUNDAY: t('enums.weekDay.SUNDAY'),
        MONDAY: t('enums.weekDay.MONDAY'),
        TUESDAY: t('enums.weekDay.TUESDAY'),
        WEDNESDAY: t('enums.weekDay.WEDNESDAY'),
        THURSDAY: t('enums.weekDay.THURSDAY'),
        FRIDAY: t('enums.weekDay.FRIDAY'),
        SATURDAY: t('enums.weekDay.SATURDAY'),
    };

    const days = Object.keys(WeekDay).map((id) => ({
        id,
        name: weekDayLabels[id] ?? WeekDay[id as keyof typeof WeekDay],
    }));

    const validationSchema = Yup.object({
        name: Yup.string().required(t('validation.nameRequired')),
        email: Yup.string().email(t('validation.emailInvalid')).required(t('validation.emailRequired')),
        phoneNumber: Yup.string()
            .required(t('validation.phoneRequired'))
            .matches(/^\+[0-9]{2,4}[0-9]{5,12}$/, t('validation.phoneInvalid')),
        language: Yup.string().required(t('validation.languageRequired')),
        currency: Yup.string().required(t('validation.currencyRequired')),
        typeOfCuisine: Yup.string().required(t('validation.cuisineRequired')),
        description: Yup.string()
            .required(t('validation.descriptionRequired'))
            .max(
                DESCRIPTION_MAX_LENGTH,
                t('validation.descriptionTooLong', { max: DESCRIPTION_MAX_LENGTH })
            ),
        billingActivationDate: Yup.string().when([], {
            is: () => isAdminRole(),
            then: (schema) => schema.required(t('validation.billingDateRequired')),
            otherwise: (schema) => schema.nullable(),
        }),
        // Admin-only field (see isAdmin-gated rendering below) - VENDOR/RESTAURANT_OWNER
        // never see or submit it, so no required/role branching is needed here.
        monthlyFee: Yup.number()
            .typeError(t('validation.monthlyFeeInvalid'))
            .min(0, t('validation.monthlyFeeInvalid'))
            .nullable(),
        addressObject: Yup.object()
            .required(t('validation.addressRequired'))
            .shape({
                streetWithNo: Yup.string().required(t('validation.streetMissing')),
                postalCode: Yup.string().required(t('validation.postalCodeMissing')),
                city: Yup.string().required(t('validation.cityMissing')),
                state: Yup.string().required(t('validation.stateMissing')),
                latitude: Yup.string().required(),
                longitude: Yup.string().required(),
            }),
    });

    const initialValues = {
        locationType: 'RESTAURANT',
        typeOfCuisine: '',
        name: '',
        phoneNumber: '',
        email: '',
        address: '',
        addressObject: {
            streetWithNo: '',
            postalCode: '',
            city: '',
            state: '',
            district: '',
            latitude: '',
            longitude: ''
        },
        language: getBackendLanguage(),
        country: '',
        currency: '',
        description: '',
        billingActivationDate: '',
        monthlyFee: '',
        openingHours: days.reduce((acc, day) => {
            acc[day.id] = { openingTime: '00:00', closingTime: '00:00' };
            return acc;
        }, {} as Record<string, { openingTime: string; closingTime: string }>)
    };

    const initialEditValues = isEditMode && data ? {
        locationType: 'RESTAURANT', //TODO: replace with real value when backend will be ready for location types
        typeOfCuisine: data.cuisineType || '',
        name: data.name || '',
        phoneNumber: data.phoneNumber || '',
        email: data.email || '',
        /* `|| ''` wiazalo sie tylko z ostatnim czlonem, wiec nigdy nie chronilo calosci. */
        address: joinAddressParts(' ', data.address?.streetWithNo, data.address?.city, data.address?.postalCode),

        addressObject: {
            streetWithNo: data.address?.streetWithNo || "",
            postalCode: data.address?.postalCode || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
            district: data.address?.district || "",
            latitude: data.address?.latitude?.toString() || "",
            longitude: data.address?.longitude?.toString() || "",
        },

        language: data.language || '',
        country: data.country || '',
        currency: data.currency || '',
        description: data.description || '',
        billingActivationDate: data.billingActivationDate || '',
        // `?? ''`, not `|| ''`: a real fee of 0 is a valid value and must not be blanked out.
        monthlyFee: data.monthlyFee ?? '',
        openingHours: data.openingHours?.reduce((acc: any, oh: any) => {
            acc[oh.dayOfWeek] = {
                openingTime: oh.openingTime || '00:00',
                closingTime: oh.closingTime || '00:00'
            };
            return acc;
        }, {}) || initialValues.openingHours
    } : initialValues;

    const handleSubmit = async (values: typeof initialValues) => {
        setIsSaving(true);

        const openingHoursData = Object.entries(values.openingHours).map(
            ([weekday, { openingTime, closingTime }]) => {
                const existingHour = data?.openingHours?.find(
                    (oh: any) => oh.dayOfWeek === weekday
                );

                return {
                    ...(isEditMode && existingHour?.id && { id: existingHour.id }),
                    weekday: weekday,
                    openingTime,
                    closingTime
                };
            }
        );

        const formData = new FormData();

        console.log("Address:", address);

        // Admin-only field: the backend ignores it for VENDOR/RESTAURANT_OWNER anyway,
        // but the key is left out of the payload entirely for them, matching the fact
        // that they never see or edit it (isAdmin-gated TextField below).
        const monthlyFeeValue =
            values.monthlyFee === '' || values.monthlyFee === null || values.monthlyFee === undefined
                ? null
                : Number(values.monthlyFee);

        /* Upsert po jezyku: pole pominiete (undefined) znika przy JSON.stringify i backend
           czyta je jako "nie ruszaj", pusty string kasuje tlumaczenie. */
        const translationsPayload = buildTranslationPayload({
            translations,
            fields: [{ key: 'description' }],
            sourceLanguage: values.language,
            sourceValues: { description: values.description },
            lastSnapshot,
        }).map((entry) => ({
            language: entry.language,
            description: entry.fields.description?.text,
            source: entry.fields.description?.source,
        }));

        const requestPayload = isEditMode ? {
            id: id,
            cuisineType: values.typeOfCuisine,
            name: values.name,
            phoneNumber: values.phoneNumber,
            autoTranslate,
            translations: translationsPayload,
            email: values.email,
            language: values.language,
            country: values.country || null,
            currency: values.currency,
            description: values.description,
            billingActivationDate: values.billingActivationDate || null,
            ...(isAdmin && { monthlyFee: monthlyFeeValue }),
            updateAddressRequest: {
                id: data?.address?.id,
                /* Bylo tu porownanie z literalem "undefined undefined" — obejscie skutku,
                   nie przyczyny, i lapalo tylko przypadek obu brakujacych czlonow.
                   Po naprawie joinAddressParts puste pole jest pustym stringiem. */
                streetWithNo: address.streetWithNo || 'No Street',
                postalCode: address.postalCode || '',
                city: address.city,
                state: address.state,
                district: address.district,
                latitude: address.latitude,
                longitude: address.longitude
            },
            updateOpeningHoursRequests: openingHoursData,
        } : {
            cuisineType: values.typeOfCuisine,
            name: values.name,
            phoneNumber: values.phoneNumber,
            email: values.email,
            language: values.language,
            country: values.country || null,
            currency: values.currency,
            description: values.description,
            autoTranslate,
            translations: translationsPayload,
            billingActivationDate: values.billingActivationDate || null,
            ...(isAdmin && { monthlyFee: monthlyFeeValue }),
            createAddressRequest: {
                streetWithNo: address.streetWithNo,
                postalCode: address.postalCode,
                city: address.city,
                state: address.state,
                district: address.district,
                latitude: address.latitude,
                longitude: address.longitude
            },
            createOpeningHoursRequest: openingHoursData,
        };

        const jsonBlob = new Blob(
            [JSON.stringify(requestPayload)],
            { type: "application/json" }
        );

        formData.append("request", jsonBlob);

        if (fileBlob) {
            formData.append("file", fileBlob);
        } else {
            formData.append("file", new Blob());
        }

        try {
            if (isEditMode) {
                console.log(requestPayload);
                await restaurantService.updateRestaurant(formData);
                notify(t('notifications.locationUpdated'), { type: 'success' });
                // Celowo bez await: tlumaczenie idzie po zapisie i trwa kilka sekund.
                void reportTranslationResult(Number(id));
            } else {
                await restaurantService.createRestaurant(formData);
                notify(t('notifications.locationCreated'), { type: 'success' });
            }
            setIsSaving(false);
            redirect("/locationsManagement");
        } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setIsSaving(false);
        }
    };

    /**
     * Dopytuje backend, czym skonczylo sie tlumaczenie po zapisie. Gdy zapis niczego nie
     * zaplanowal (komplet tlumaczen byl aktualny), milczy.
     */
    const reportTranslationResult = async (locationId: number) => {
        try {
            const result = await watchTranslations(() => restaurantService.getRestaurants(locationId));

            if (!result.wasPending) return;

            if (result.status === 'FAILED') {
                notify(
                    t('notifications.translationsFailed', {
                        languages: result.failedLanguages.map((code) => t(`enums.language.${code}`)).join(', '),
                    }),
                    { type: 'warning' },
                );
            } else if (result.status === 'PENDING') {
                notify(t('notifications.translationsStillRunning'), { type: 'info' });
            } else {
                notify(t('notifications.translationsReady'), { type: 'success' });
            }
        } catch (error) {
            // Sam zapis sie udal — nieudane odpytanie o status nie jest bledem dla uzytkownika.
            console.error('Failed to read translation status', error);
        }
    };

    const isMobile = useMediaQuery('(max-width:600px)');

    //TODO: style it (CircularProgress in the center of the form)
    if (!isLoaded || (isEditMode && isLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Formik
                initialValues={initialEditValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, handleChange, errors, touched, isValid, setFieldValue, setFieldTouched, setValues, handleBlur }) => {

                    const addressError =
                        errors.addressObject?.streetWithNo ||
                        errors.addressObject?.postalCode ||
                        errors.addressObject?.city ||
                        errors.addressObject?.state;


                    const handleOnPlacesChanged = async () => {
                        console.log('changed');
                        const places = (addressInputRef.current as any)?.getPlaces();
                        if (places && places.length > 0) {
                            const place = places[0];

                            const lat = place.geometry?.location?.lat();
                            const lng = place.geometry?.location?.lng();


                            const getAddressComponent = (types: string[]) => {
                                const component = place.address_components?.find((comp: any) =>
                                    types.some(type => comp.types.includes(type))
                                );
                                return component?.long_name;
                            };

                            const streetNumber = getAddressComponent(['street_number']);
                            const route = getAddressComponent(['route']);
                            const streetWithNo = joinAddressParts(' ', route, streetNumber);

                            const addressDetails = {
                                streetWithNo: streetWithNo,
                                postalCode: getAddressComponent(['postal_code']),
                                city: getAddressComponent(['locality']) || getAddressComponent(['postal_town']),
                                state: getAddressComponent(['administrative_area_level_1']),
                                district: getAddressComponent(['sublocality']) || getAddressComponent(['administrative_area_level_2']),
                                latitude: lat.toString(),
                                longitude: lng.toString()
                            };

                            const formattedAddress =
                                place.formatted_address ||
                                joinAddressParts(', ', streetWithNo, addressDetails.city, addressDetails.postalCode);

                            // Kraj z adresu Google (short_name = kod ISO, np. "DE") - z nim backend zaklada
                            // konto Stripe restauracji. Bez pola w formularzu i bez wplywu na walute.
                            const placeCountry = place.address_components?.find((comp: any) =>
                                comp.types.includes('country')
                            )?.short_name;
                            const countryValues = isSupportedCountry(placeCountry) ? { country: placeCountry } : {};

                            setAddress(addressDetails);
                            await setValues(
                                { ...values, ...countryValues, address: formattedAddress, addressObject: addressDetails },
                                true
                            );
                            lastSelectedAddressRef.current = formattedAddress;
                            setFieldTouched("address", true, false);
                            console.log('Address details:', addressDetails);

                            const isComplete = addressDetails.streetWithNo &&
                                addressDetails.postalCode &&
                                addressDetails.city &&
                                addressDetails.state &&
                                addressDetails.latitude &&
                                addressDetails.longitude;
                            if (isComplete) {
                                setIsAddressComplete(true);
                            }
                        }
                    };

                    return (
                        <Form>
                            {/* Schemat waliduje addressObject.*, ale widocznym polem jest `address`. */}
                            <ScrollToFirstError fieldAliases={{ addressObject: 'address' }} />
                            <PageHeader
                                back={<FormBackButton />}
                                title={isEditMode ? t('locationsManagement.editLocation') : t('locationsManagement.addLocation')}
                            />
                            <Box
                                sx={{
                                    px: PAGE_GUTTER,
                                    py: { xs: 2, md: 4 },
                                    display: 'flex',
                                    gap: { xs: 'var(--space-6)', md: 'var(--space-8)' },
                                    flexDirection: { xs: 'column', md: 'row' },
                                    ...labelAboveSx,
                                }}
                            >
                                {/* Opening hours — last on mobile */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: { xs: 'none', md: 0.5 },
                                        order: { xs: 3, md: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 'var(--text-sm)',
                                            mb: 2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t('locationsManagement.create.openingHours')}
                                    </Typography>

                                    <Stack spacing={isMobile ? 2 : 3}>
                                        {days.map((day) => (
                                            <Stack
                                                key={day.id}
                                                direction="row"
                                                alignItems="center"
                                                spacing={isMobile ? 1 : 2}
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        fontSize: 'var(--text-xs)',
                                                        color: 'text.primary',
                                                        minWidth: isMobile ? 60 : 80,
                                                    }}
                                                >
                                                    {isMobile ? day.name.slice(0, 3) : day.name}
                                                </Typography>

                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    name={`openingHours.${day.id}.openingTime`}
                                                    value={values.openingHours[day.id].openingTime}
                                                    onChange={handleChange}
                                                    sx={isMobile ? { '& input': { fontSize: 'var(--text-xs)', px: 1 } } : {}}
                                                />
                                                <TextField
                                                    type="time"
                                                    size="small"
                                                    name={`openingHours.${day.id}.closingTime`}
                                                    value={values.openingHours[day.id].closingTime}
                                                    onChange={handleChange}
                                                    sx={isMobile ? { '& input': { fontSize: 'var(--text-xs)', px: 1 } } : {}}
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                                {/* Form fields — first on mobile */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: { xs: 'none', md: 1 },
                                        order: { xs: 1, md: 2 },
                                    }}
                                >
                                    <Stack spacing={0}>
                                        <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.locationType && Boolean(errors.locationType)}>
                                            <InputLabel>{t('locationsManagement.create.locationType')}</InputLabel>
                                            <Select displayEmpty name="locationType"
                                                value={values.locationType}
                                                onChange={handleChange}>
                                                <MenuItem value="" disabled>
                                                    {t('locationsManagement.create.locationType')}
                                                </MenuItem>
                                                {LOCATION_TYPE.map((type: { id: string; name: string }) => (
                                                    <MenuItem key={type.id} value={type.id}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.locationType && errors.locationType ? errors.locationType : " "}
                                            </FormHelperText>
                                        </FormControl>

                                        <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.typeOfCuisine && Boolean(errors.typeOfCuisine)}>
                                            <InputLabel>{t('locationsManagement.create.typeOfCuisine')}</InputLabel>
                                            <Select displayEmpty name="typeOfCuisine"
                                                value={values.typeOfCuisine}
                                                onChange={handleChange}>
                                                <MenuItem value="" disabled>
                                                    {t('locationsManagement.create.typeOfCuisine')}
                                                </MenuItem>
                                                {TYPE_OF_CUISINE.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.typeOfCuisine && errors.typeOfCuisine ? errors.typeOfCuisine : " "}
                                            </FormHelperText>
                                        </FormControl>

                                        <TextField
                                            name="name"
                                            size="small"
                                            label={t('locationsManagement.create.nameOfLocation')}
                                            fullWidth
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name ? errors.name : " "}
                                        />
                                        <TextField
                                            name="phoneNumber"
                                            size="small"
                                            label={t('locationsManagement.create.phoneNumber')}
                                            fullWidth
                                            value={values.phoneNumber}
                                            onChange={handleChange}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                            helperText={touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : " "}
                                        />
                                        <TextField
                                            name="email"
                                            size="small"
                                            label={t('locationsManagement.create.email')}
                                            fullWidth
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email ? errors.email : " "}
                                        />
                                        <StandaloneSearchBox
                                            onLoad={ref => (addressInputRef.current = ref)}
                                            onPlacesChanged={handleOnPlacesChanged}
                                        >
                                            <TextField
                                                name="address"
                                                size="small"
                                                label={t('locationsManagement.create.address')}
                                                fullWidth
                                                value={values.address}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    if (e.target.value !== lastSelectedAddressRef.current) {
                                                        setIsAddressComplete(false);
                                                        setFieldValue("addressObject", {
                                                            streetWithNo: '',
                                                            postalCode: '',
                                                            city: '',
                                                            state: '',
                                                            district: '',
                                                            latitude: '',
                                                            longitude: ''
                                                        });
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                                error={touched.address && Boolean(addressError) && !isAddressComplete}
                                                helperText={touched.address && addressError && !isAddressComplete ? addressError : " "}
                                            />
                                        </StandaloneSearchBox>

                                        <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.language && Boolean(errors.language)}>
                                            <InputLabel>{t('locationsManagement.create.language')}</InputLabel>
                                            <Select
                                                displayEmpty
                                                name="language"
                                                value={values.language}
                                                onBlur={handleBlur}
                                                onChange={handleChange}>
                                                <MenuItem value="" disabled>
                                                    {t('locationsManagement.create.language')}
                                                </MenuItem>
                                                {LANGUAGE.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>

                                            <FormHelperText>
                                                {touched.language && errors.language ? errors.language : " "}
                                            </FormHelperText>
                                        </FormControl>

                                        <FormControl
                                            size="small"
                                            fullWidth
                                            error={touched.currency && Boolean(errors.currency)}>
                                            <InputLabel>{t('locationsManagement.create.currency')}</InputLabel>
                                            <Select
                                                displayEmpty
                                                name="currency"
                                                // Walute ustawia tylko admin (backend ignoruje ja od vendora).
                                                disabled={!isAdmin}
                                                value={values.currency}
                                                onBlur={handleBlur}
                                                onChange={handleChange}>
                                                <MenuItem value="" disabled>
                                                    {t('locationsManagement.create.currency')}
                                                </MenuItem>
                                                {CURRENCY_CODE.map(c => (
                                                    <MenuItem key={c.id} value={c.value}>{c.value}</MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {touched.currency && errors.currency ? errors.currency : " "}
                                            </FormHelperText>
                                        </FormControl>

                                        {isAdmin && (
                                            <TextField
                                                name="billingActivationDate"
                                                type="date"
                                                size="small"
                                                label={t('locationsManagement.create.billingActivationDate')}
                                                fullWidth
                                                value={values.billingActivationDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.billingActivationDate && Boolean(errors.billingActivationDate)}
                                                helperText={touched.billingActivationDate && errors.billingActivationDate ? errors.billingActivationDate : " "}
                                                slotProps={{ inputLabel: { shrink: true } }}
                                            />
                                        )}

                                        {isAdmin && (
                                            <TextField
                                                name="monthlyFee"
                                                type="number"
                                                size="small"
                                                label={values.currency
                                                    ? `${t('locationsManagement.create.restaurantFee')} (${values.currency})`
                                                    : t('locationsManagement.create.restaurantFee')}
                                                fullWidth
                                                value={values.monthlyFee}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.monthlyFee && Boolean(errors.monthlyFee)}
                                                helperText={touched.monthlyFee && errors.monthlyFee
                                                    ? errors.monthlyFee
                                                    : monthlyFeeHint(values.currency)}
                                                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                                            />
                                        )}

                                        <TextField
                                            name="description"
                                            label={t('locationsManagement.create.description')}
                                            multiline
                                            rows={3}
                                            fullWidth
                                            value={values.description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description ? errors.description : `${values.description.length} / ${DESCRIPTION_MAX_LENGTH}`}
                                            sx={{
                                                '& .MuiInputBase-root': { padding: '10px 14px' }
                                            }}
                                        />

                                        <TranslationsSection
                                            languages={LANGUAGE}
                                            sourceLanguage={values.language}
                                            fields={[
                                                {
                                                    key: 'description',
                                                    label: t('locationsManagement.create.description'),
                                                    maxLength: DESCRIPTION_MAX_LENGTH,
                                                    multiline: true,
                                                },
                                            ]}
                                            sourceValues={{ description: values.description }}
                                            translations={translations}
                                            lastSnapshot={lastSnapshot}
                                            onApply={(next, snapshot) => {
                                                setTranslations(next);
                                                setLastSnapshot(snapshot);
                                            }}
                                            autoTranslate={autoTranslate}
                                            onAutoTranslateChange={setAutoTranslate}
                                            requestAiTranslation={() => requestAiTranslation(values.language, values.description)}
                                        />

                                    </Stack>
                                </Box>
                                {/* Image upload — second on mobile */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: { xs: 'none', md: 1 },
                                        order: { xs: 2, md: 3 },
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        onDrop={handleDrop}
                                        onDragOver={(e) => e.preventDefault()}
                                        sx={{
                                            width: '100%',
                                            height: { xs: 200, md: 360 },
                                            border: '1px dashed var(--color-secondary)',
                                            borderRadius: 'var(--radius-xs)',
                                            backgroundColor: 'var(--color-drop-surface)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'text.secondary',
                                            fontSize: 'var(--text-xs)',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'var(--color-drop-surface-active)',
                                            },
                                        }}
                                        onClick={() => document.getElementById('fileInput')?.click()}
                                    >
                                        <input
                                            id="fileInput"
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            hidden
                                            onChange={handleFileSelect}
                                        />

                                        {previewUrl ? (
                                            <Box sx={{ textAlign: 'center' }}>
                                                <img
                                                    src={previewUrl}
                                                    alt="preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '360px',
                                                        overflow: 'hidden',
                                                        borderRadius: '4px',
                                                        objectFit: 'fill',
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                {/* Drag & drop does not exist on a phone — tell touch users what works. */}
                                                <Typography sx={{ mb: 0.5 }}>{isMobile ? t('locationsManagement.create.tapToChooseFile') : t('locationsManagement.create.dropFileHere')}</Typography>
                                            </>
                                        )}
                                    </Box>

                                    {fileBlob && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ mt: 2 }}
                                        >
                                            <IconButton
                                                icon={<EditIcon />}
                                                onClick={() => {
                                                    if (rawImageSrc) {
                                                        setCropDialogOpen(true);
                                                    } else {
                                                        document.getElementById('fileInput')?.click();
                                                    }
                                                }}
                                            />

                                            <IconButton
                                                icon={<DeleteOutlineIcon />}
                                                color="var(--color-error)"
                                                onClick={handleRemove}
                                            />
                                        </Stack>
                                    )}

                                    <ImageCropDialog
                                        open={cropDialogOpen}
                                        imageSrc={rawImageSrc}
                                        aspect={16 / 9}
                                        onClose={handleCropCancel}
                                        onCropComplete={handleCropComplete}
                                    />

                                    {/* desktop buttons — hidden on mobile */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            mt: 'var(--space-6)',
                                            display: { xs: 'none', md: 'flex' },
                                            gap: 'var(--space-8)',
                                            justifyContent: 'flex-end',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            type="button"
                                            onClick={() => redirect('/locationsManagement')}
                                            sx={{
                                                borderRadius: 'var(--radius-sm)',
                                                borderColor: 'var(--color-error)',
                                                color: 'var(--color-error)',
                                                transition: 'background-color 200ms ease, border-color 200ms ease',
                                                '&:hover': {
                                                    borderColor: 'var(--color-error-hover)',
                                                    backgroundColor: 'var(--color-error-soft)',
                                                    color: 'var(--color-error-hover)',
                                                },
                                            }}
                                        >
                                            {t('common.cancel')}
                                        </Button>

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={isSaving}
                                            sx={{
                                                borderRadius: 'var(--radius-sm)',
                                                backgroundColor: 'var(--color-primary)',
                                                color: 'var(--color-text-inverse)',
                                                boxShadow: 'none',
                                                transition: 'background-color 200ms ease',
                                                '&:hover': { backgroundColor: 'var(--color-primary-hover)', color: 'var(--color-text-inverse)' },
                                            }}
                                        >
                                            {isSaving ? <CircularProgress size={25} sx={{ color: 'white' }} /> : isEditMode ? t('common.saveChanges') : t('common.add')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    px: PAGE_GUTTER,
                                    pb: { xs: 2, md: 0 },
                                    display: { xs: 'flex', md: 'none' },
                                    gap: 'var(--space-3)',
                                    flexDirection: 'column',
                                }}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSaving}
                                    sx={{
                                        borderRadius: 'var(--radius-sm)',
                                        backgroundColor: 'var(--color-primary)',
                                        color: 'var(--color-text-inverse)',
                                        boxShadow: 'none',
                                        transition: 'background-color 200ms ease',
                                        '&:hover': { backgroundColor: 'var(--color-primary-hover)', color: 'var(--color-text-inverse)' },
                                    }}
                                >
                                    {isSaving ? <CircularProgress size={25} sx={{ color: 'white' }} /> : isEditMode ? t('common.saveChanges') : t('common.add')}
                                </Button>

                                <Button
                                    variant="text"
                                    type="button"
                                    onClick={() => redirect('/locationsManagement')}
                                    sx={{
                                        borderRadius: 'var(--radius-sm)',
                                        color: 'var(--color-text-secondary)',
                                        '&:hover': {
                                            backgroundColor: 'var(--color-bg-subtle)',
                                            color: 'var(--color-text-primary)',
                                        },
                                    }}
                                >
                                    {t('common.cancel')}
                                </Button>
                            </Box>
                        </Form>
                    )
                }}
            </Formik>
            {/* Outside the Formik form: its buttons must not submit the location form. */}
            {isEditMode && id && (
                <Box sx={{ px: PAGE_GUTTER, pb: 'var(--space-8)' }}>
                    <DotykackaSection locationId={id} locationName={data?.name} />
                </Box>
            )}
        </>
    );
}
